import React, { useEffect, useState } from 'react'
import { Show } from '@refinedev/antd'
import { useShow, useUpdate, useGetIdentity } from '@refinedev/core'
import { App, Button, Card, Col, Divider, Form, Input, Modal, Row, Space, Typography } from 'antd'
import { Link } from 'react-router'
import { brand } from '../../theme/brand'
import { Pill, type Tone } from '../../theme/listkit'
import { ModerationActions } from '../profiles/ModerationActions'
import { ReportedContent } from './ReportedContent'
import { headStyle, labelStyle, Field, fmtDate } from './reportUiKit'
import { groupsAdminErrorMessage } from '../groups/adminErrors'
import { isGroupReportTarget, groupShowPath, resolveReportGroupId, suspendReportedGroup } from './groupReportLink'
import { reportTargetLink } from './reportTargetLink'

export interface Report {
  id: string
  reporter_id: string | null
  target_type:
    | 'post'
    | 'photo'
    | 'avatar'
    | 'profile'
    | 'job'
    | 'message'
    | 'business'
    | 'business_vouch'
    | 'business_note'
    | 'business_request'
    | 'business_reply'
    | 'group'
    | 'group_meetup'
    | 'memory_comment'
  target_id: string
  target_user_id: string | null
  reason: 'spam' | 'harassment' | 'hate' | 'nudity' | 'violence' | 'misinfo' | 'other'
  details: string | null
  source: 'member' | 'auto_image'
  status: 'open' | 'reviewing' | 'actioned' | 'dismissed'
  reviewed_by: string | null
  reviewed_at: string | null
  resolution_note: string | null
  created_at: string
}

export interface Identity {
  id?: string
  name?: string
}

const reasonTone = (reason: string): Tone => {
  if (['hate', 'harassment', 'violence', 'nudity'].includes(reason)) return 'red'
  if (['spam', 'misinfo'].includes(reason)) return 'gold'
  return 'neutral'
}

const statusTone = (status: string): Tone => {
  if (status === 'open') return 'gold'
  if (status === 'reviewing') return 'jaguar'
  if (status === 'actioned') return 'green'
  return 'neutral'
}

/**
 * Report detail + action surface.
 *
 * Status transitions:
 *   "Mark reviewing"  → status=reviewing
 *   "Dismiss"         → status=dismissed + resolution_note via modal
 *   "Action author"   → calls admin-action Edge Function (via ModerationActions component),
 *                       then sets status=actioned + reviewed_by + reviewed_at + resolution_note
 *
 * reviewed_by is set to the currently logged-in admin's identity id.
 */
export function ReportShow() {
  const { query } = useShow<Report>()
  const { data, isLoading } = query
  const record = data?.data

  const { data: identity } = useGetIdentity<Identity>()
  const { mutate: update } = useUpdate()
  const [isUpdating, setIsUpdating] = React.useState(false)
  const { message, modal } = App.useApp()

  // "Dismiss" modal state
  const [dismissOpen, setDismissOpen] = useState(false)
  const [dismissForm] = Form.useForm<{ resolution_note: string }>()

  // "Action author" modal state — shows ModerationActions controls inside a modal
  // so the admin can pick the action type, then we mark the report actioned.
  const [actionOpen, setActionOpen] = useState(false)
  const [actionNote, setActionNote] = useState('')
  const [actionTaken, setActionTaken] = useState(false)

  // ── Reported group (target_type 'group' | 'group_meetup') ──────────────
  //
  // A 'group' report names the group directly — target_id IS the group id.
  // A 'group_meetup' report names a MEETUP; group_meetups isn't a registered
  // Refine resource (no admin CRUD page of its own yet), so this resolves
  // group_id with one direct supabaseClient query, the same ad hoc-query
  // pattern GroupMembersTable.tsx already uses for cross-table reads the
  // data provider's resource registry doesn't cover. Suspend calls the same
  // `set_group_status` RPC — and reuses the same error-message map — as the
  // Group Show page's own Suspend button (groups/show.tsx).
  const [groupId, setGroupId] = useState<string | null>(null)
  const [groupIdLoading, setGroupIdLoading] = useState(false)
  const [suspending, setSuspending] = useState(false)

  useEffect(() => {
    if (!record || !isGroupReportTarget(record.target_type)) {
      setGroupId(null)
      return
    }
    let cancelled = false
    setGroupIdLoading(true)
    setGroupId(null)
    resolveReportGroupId(record)
      .then((id) => {
        if (!cancelled) setGroupId(id)
      })
      .finally(() => {
        if (!cancelled) setGroupIdLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [record])

  const suspendGroup = () => {
    if (!groupId) return
    setSuspending(true)
    suspendReportedGroup(groupId)
      .then(() => message.success('Group suspended'))
      .catch((e) => message.error(groupsAdminErrorMessage(e)))
      .finally(() => setSuspending(false))
  }

  const confirmSuspendGroup = () => {
    if (!record) return
    modal.confirm({
      title: 'Suspend this group?',
      content:
        record.target_type === 'group_meetup'
          ? "The group this meetup belongs to will be hidden from non-members and its chat frozen."
          : 'The group will be hidden from non-members and its chat frozen.',
      okText: 'Suspend',
      okButtonProps: { danger: true },
      onOk: suspendGroup,
    })
  }

  const stamp = () => ({
    reviewed_by: identity?.id ?? null,
    reviewed_at: new Date().toISOString(),
  })

  const markReviewing = () => {
    if (!record) return
    setIsUpdating(true)
    update(
      {
        resource: 'reports',
        id: record.id,
        values: { status: 'reviewing', ...stamp() },
      },
      {
        onSuccess: () => { setIsUpdating(false); message.success('Marked as reviewing') },
        onError: (e) => { setIsUpdating(false); message.error('Update failed: ' + String(e)) },
      },
    )
  }

  const handleDismiss = (values: { resolution_note: string }) => {
    if (!record) return
    setIsUpdating(true)
    update(
      {
        resource: 'reports',
        id: record.id,
        values: { status: 'dismissed', resolution_note: values.resolution_note, ...stamp() },
      },
      {
        onSuccess: () => {
          setIsUpdating(false)
          message.success('Report dismissed')
          setDismissOpen(false)
          dismissForm.resetFields()
        },
        onError: (e) => { setIsUpdating(false); message.error('Update failed: ' + String(e)) },
      },
    )
  }

  const handleMarkActioned = () => {
    if (!record) return
    setIsUpdating(true)
    update(
      {
        resource: 'reports',
        id: record.id,
        values: {
          status: 'actioned',
          resolution_note: actionNote || 'Author actioned via admin panel.',
          ...stamp(),
        },
      },
      {
        onSuccess: () => {
          setIsUpdating(false)
          message.success('Report marked actioned')
          setActionOpen(false)
          setActionNote('')
          setActionTaken(false)
        },
        onError: (e) => { setIsUpdating(false); message.error('Update failed: ' + String(e)) },
      },
    )
  }

  const isResolved = record?.status === 'actioned' || record?.status === 'dismissed'
  const targetLink = record ? reportTargetLink(record.target_type, record.target_id) : null

  return (
    <Show isLoading={isLoading}>
      {/* ── Hero strip ── */}
      <div
        style={{
          background: brand.navyGrad,
          borderRadius: 16,
          padding: '22px 28px',
          marginBottom: 16,
          border: `1px solid ${brand.navy700}`,
        }}
      >
        <Row align="middle" gutter={[24, 12]}>
          <Col flex="auto">
            <div style={{ fontFamily: brand.fSans, fontWeight: 600, fontSize: 11, color: 'rgba(255,255,255,.5)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: 6 }}>
              Report
            </div>
            <Space size={8} wrap>
              {record?.source === 'auto_image' ? (
                <Pill tone="jaguar">Auto-image</Pill>
              ) : null}
              {record?.target_type ? <Pill tone="jaguar">{record.target_type}</Pill> : null}
              {record?.reason ? <Pill tone={reasonTone(record.reason)}>{record.reason}</Pill> : null}
              {record?.status ? <Pill tone={statusTone(record.status)}>{record.status}</Pill> : null}
            </Space>
          </Col>
          <Col flex="none">
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontFamily: brand.fSans, fontSize: 11, color: 'rgba(255,255,255,.5)', textTransform: 'uppercase', letterSpacing: '.08em' }}>
                Reported
              </div>
              <div style={{ fontFamily: brand.fSans, fontWeight: 700, fontSize: 14, color: brand.gold3, marginTop: 3 }}>
                {fmtDate(record?.created_at ?? null)}
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* ── Report details ── */}
      <Card title={<span style={headStyle}>Report details</span>} style={{ marginBottom: 16, borderColor: brand.cardLine }}>
        <Row gutter={[28, 8]}>
          <Col xs={24} sm={12}>
            <Field label="Target type">
              <Space size={10} align="center">
                <span>{record?.target_type ?? '—'}</span>
                {targetLink ? (
                  <Link to={targetLink.to} data-testid="report-target-link">
                    {targetLink.label}
                  </Link>
                ) : null}
              </Space>
            </Field>
            <Field label="Target ID">
              <Typography.Text
                copyable={!!record?.target_id}
                style={{ fontFamily: brand.fSans, fontSize: 12.5, color: brand.sub }}
              >
                {record?.target_id ?? '—'}
              </Typography.Text>
            </Field>
            <Field label="Target user (author)">
              {record?.target_user_id ? (
                <Typography.Text
                  copyable
                  style={{ fontFamily: brand.fSans, fontSize: 12.5, color: brand.sub }}
                >
                  {record.target_user_id}
                </Typography.Text>
              ) : (
                <span style={{ color: brand.sub }}>—</span>
              )}
            </Field>
          </Col>
          <Col xs={24} sm={12}>
            <Field label="Reporter">
              {record?.source === 'auto_image' || !record?.reporter_id ? (
                <span
                  style={{
                    fontFamily: brand.fSans,
                    fontSize: 12.5,
                    color: brand.sub,
                    fontStyle: 'italic',
                  }}
                >
                  Auto-moderation (Rekognition)
                </span>
              ) : (
                <Typography.Text
                  copyable
                  style={{ fontFamily: brand.fSans, fontSize: 12.5, color: brand.sub }}
                >
                  {record.reporter_id}
                </Typography.Text>
              )}
            </Field>
            <Field label="Reason">{record?.reason ?? '—'}</Field>
            <Field label="Status">{record?.status ?? '—'}</Field>
          </Col>
        </Row>

        {record?.details ? (
          <>
            <Divider style={{ margin: '14px 0' }} />
            <div style={labelStyle}>
              {record.source === 'auto_image' ? 'Auto-detection summary' : 'Reporter note'}
            </div>
            <Typography.Paragraph
              style={{ fontFamily: brand.fSans, fontSize: 13.5, color: brand.ink, whiteSpace: 'pre-wrap', marginBottom: 0 }}
            >
              {record.details}
            </Typography.Paragraph>
          </>
        ) : null}

        {isResolved ? (
          <>
            <Divider style={{ margin: '14px 0' }} />
            <div style={labelStyle}>Resolution</div>
            <Typography.Paragraph style={{ fontFamily: brand.fSans, fontSize: 13.5, color: brand.ink, marginBottom: 4 }}>
              {record?.resolution_note ?? '—'}
            </Typography.Paragraph>
            <div style={{ fontFamily: brand.fSans, fontSize: 11.5, color: brand.sub }}>
              Reviewed {fmtDate(record?.reviewed_at ?? null)}
              {record?.reviewed_by ? ` by ${record.reviewed_by.slice(0, 8)}…` : ''}
            </div>
          </>
        ) : null}
      </Card>

      <ReportedContent report={record} />

      {/* ── Reported group (target_type 'group' | 'group_meetup') ── */}
      {record && isGroupReportTarget(record.target_type) ? (
        <Card title={<span style={headStyle}>Reported group</span>} style={{ marginBottom: 16, borderColor: brand.cardLine }}>
          <Space wrap align="center">
            {groupId ? (
              <Link to={groupShowPath(groupId)} data-testid="report-view-group">
                <Button>
                  {record.target_type === 'group_meetup' ? "View the meetup's group" : 'View group'}
                </Button>
              </Link>
            ) : (
              <Button disabled loading={groupIdLoading}>
                {groupIdLoading ? 'Resolving group…' : 'Group not found'}
              </Button>
            )}
            <Button
              data-testid="report-suspend-group"
              danger
              disabled={!groupId}
              loading={suspending}
              onClick={confirmSuspendGroup}
            >
              Suspend group
            </Button>
          </Space>
        </Card>
      ) : null}

      {/* ── Triage actions ── */}
      {!isResolved ? (
        <Card title={<span style={headStyle}>Triage actions</span>} style={{ marginBottom: 16, borderColor: brand.cardLine }}>
          <Typography.Text style={{ fontFamily: brand.fSans, fontSize: 13, color: brand.sub, display: 'block', marginBottom: 14 }}>
            Move this report through the review workflow or take action against the content author.
          </Typography.Text>
          <Space wrap>
            <Button
              onClick={markReviewing}
              loading={isUpdating}
              disabled={record?.status === 'reviewing'}
              type="default"
            >
              Mark reviewing
            </Button>
            <Button onClick={() => setDismissOpen(true)} disabled={isUpdating}>
              Dismiss
            </Button>
            {record?.target_user_id ? (
              <Button type="primary" danger onClick={() => setActionOpen(true)} disabled={isUpdating}>
                Action author
              </Button>
            ) : null}
          </Space>
        </Card>
      ) : null}

      {/* ── Author integrity (always visible when target_user_id exists) ── */}
      {record?.target_user_id ? (
        <Card title={<span style={headStyle}>Author integrity</span>} style={{ borderColor: brand.cardLine }}>
          <Typography.Text style={{ fontFamily: brand.fSans, fontSize: 12.5, color: brand.sub, display: 'block', marginBottom: 12 }}>
            Integrity actions for the author of the reported content. Each action is logged to the
            Moderation Log and enforced via Supabase Auth / GetStream.
          </Typography.Text>
          <ModerationActions userId={record.target_user_id} />
        </Card>
      ) : null}

      {/* ── Dismiss modal ── */}
      <Modal
        title="Dismiss report"
        open={dismissOpen}
        onCancel={() => { setDismissOpen(false); dismissForm.resetFields() }}
        onOk={() => dismissForm.submit()}
        okText="Dismiss"
        confirmLoading={isUpdating}
        destroyOnClose
      >
        <Form form={dismissForm} layout="vertical" onFinish={handleDismiss}>
          <Form.Item
            name="resolution_note"
            label="Resolution note"
            rules={[{ required: true, message: 'Please add a note explaining the dismissal' }]}
          >
            <Input.TextArea rows={3} placeholder="e.g. Content does not violate policy after review." />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Action author modal ── */}
      <Modal
        title="Action report author"
        open={actionOpen}
        onCancel={() => { setActionOpen(false); setActionNote(''); setActionTaken(false) }}
        footer={[
          <Button key="cancel" onClick={() => { setActionOpen(false); setActionNote(''); setActionTaken(false) }}>
            Cancel
          </Button>,
          <Button
            key="done"
            type="primary"
            disabled={!actionTaken}
            loading={isUpdating}
            onClick={handleMarkActioned}
          >
            Mark report actioned
          </Button>,
        ]}
        destroyOnClose
        width={560}
      >
        <Typography.Text style={{ fontFamily: brand.fSans, fontSize: 13, color: brand.sub, display: 'block', marginBottom: 14 }}>
          Use the controls below to take an integrity action against the content author, then mark this
          report as actioned. You must apply at least one action before closing.
        </Typography.Text>
        <ModerationActions userId={record?.target_user_id ?? undefined} />
        <Divider style={{ margin: '16px 0' }} />
        <Form layout="vertical">
          <Form.Item label="Resolution note">
            <Input.TextArea
              rows={2}
              placeholder="Describe the action taken…"
              value={actionNote}
              onChange={(e) => setActionNote(e.target.value)}
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="default"
              onClick={() => setActionTaken(true)}
              disabled={actionTaken}
            >
              {actionTaken ? 'Action applied — ready to close' : 'Confirm action applied above'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </Show>
  )
}
